import { useState } from 'react';
import { Navigate } from 'react-router-dom';

import { FeatureFlagName, useHasFeatureFlag, useHasRole } from 'hooks/users';
import ChangeOrganization from 'components/dashboard/ChangeOrganization';

import DefaultLayout from '../layout/DefaultLayout';
import { CreditTransactionsTable } from './CreditTransactionsTable';
import { useOrganizationCredits } from 'hooks/backend/billing';
import { useCurrentOrganization } from 'hooks/backend/organizations';
import FormGroup from 'components/common/forms/FormGroup';
import CircleLoader from 'components/common/CircleLoader';
import Button from 'components/common/forms/Button';
import Icon from 'components/common/Icon';
import { BuyCreditsModal } from './BuyCreditsModal';
import { AddCreditsModal } from './AddCreditsModal';

export const Billing = () => {
  const isAdmin = useHasRole('admin');
  const currentOrg = useCurrentOrganization();
  const { isLoadingCredits, orgCredits } = useOrganizationCredits({
    organizationId: currentOrg?.id,
  });
  const [isAddCreditsModalOpen, setIsAddCreditsModalOpen] = useState(false);
  const [isBuyCreditsModalOpen, setIsBuyCreditsModalOpen] = useState(false);

  const showBillingPage = useHasFeatureFlag(FeatureFlagName.BILLING_PAGE);
  if (!showBillingPage) {
    return <Navigate replace to={'/'} />;
  }

  return (
    <DefaultLayout>
      <div className="min-h-full pt-6 pb-32 px-8 bg-gray-50">
        <div className="flex items-start justify-between mb-10">
          <div className="gap-2">
            <div className="text-xl text-primary-d-600 font-medium mb-8">
              Billing Overview
            </div>
            <div className="flex m-4 mb-8 justify-between items-center">
              <div className="flex items-center rounded-xl border border-gray-d-200 p-4 gap-8">
                <FormGroup label="Organization">
                  <ChangeOrganization />
                </FormGroup>
                <FormGroup label="Current credits">
                  {currentOrg ? (
                    <div className="flex text-m text-gray-d-600">
                      <div className="mx-2">
                        {isLoadingCredits ? (
                          <CircleLoader isColored={true} />
                        ) : (
                          orgCredits?.totalCreditAmount
                        )}
                      </div>
                    </div>
                  ) : (
                    <span className="text-sm text-gray-d-600">
                      No organization selected
                    </span>
                  )}
                </FormGroup>
              </div>
              {currentOrg?.useCredits && (
                <div>
                  <Button
                    disabled={!currentOrg}
                    hierarchy="primary"
                    icon={<Icon id="plus" />}
                    iconPlacement="leading"
                    onClick={() => {
                      if (isAdmin) {
                        setIsAddCreditsModalOpen(true);
                      } else {
                        setIsBuyCreditsModalOpen(true);
                      }
                    }}
                    size="sm"
                    type="button"
                  >
                    {`${isAdmin ? 'Add' : 'Purchase'} credits`}
                  </Button>
                </div>
              )}
            </div>
            {currentOrg?.useCredits ? (
              <CreditTransactionsTable />
            ) : (
              <div>
                This organization is not setup to use credits. Please contact a
                Glass administrator for more information.
              </div>
            )}
          </div>
        </div>
      </div>
      {isBuyCreditsModalOpen && currentOrg && !isAdmin && (
        <BuyCreditsModal
          onCloseModal={() => setIsBuyCreditsModalOpen(false)}
          organizationId={currentOrg.id}
        />
      )}
      {isAddCreditsModalOpen && currentOrg && isAdmin && (
        <AddCreditsModal
          onCloseModal={() => setIsAddCreditsModalOpen(false)}
          orgCredits={orgCredits}
          organization={currentOrg}
        />
      )}
    </DefaultLayout>
  );
};
